import type {
  Account,
  ImpersonationSelection,
  ImpersonationState,
  Persona,
} from "./types";
import { isOnboardedTenant } from "./hooks/useGetTenant";

/**
 * Returns the pending selection if available, otherwise the current selection.
 * Used to get the "working" state that accounts for in-progress changes.
 */
export function getWorkingSelection(
  state: Pick<ImpersonationState, "pending" | "current">,
): ImpersonationSelection | null {
  return state.pending ?? state.current ?? null;
}

export function initializePendingFromCurrent(
  state: ImpersonationState,
): ImpersonationState {
  if (state.pending || !state.current) {
    return state;
  }

  // Backfill the cache for a current selection that may have been set
  // externally (e.g. synced from the live identity) rather than through a selector.
  const seededState = setPersonaForAccount(
    state,
    state.current.account?.id ?? null,
    state.current.user,
  );

  return {
    ...seededState,
    pending: { ...state.current },
  };
}

/**
 * Updates the userByAccountId cache with a persona for a specific account.
 * Protects against virtual admin personas overwriting real user selections.
 * Does not update the cache if accountId or persona is null (early return).
 * @param state - Current impersonation state
 * @param accountId - The account to associate with the persona; if null, state is unchanged
 * @param persona - The user to cache; if null, state is unchanged
 */
export function setPersonaForAccount(
  state: ImpersonationState,
  accountId: number | null,
  persona: Persona | null,
): ImpersonationState {
  if (accountId == null || persona == null) {
    return state;
  }

  const existingPersona = state.userByAccountId?.[accountId] ?? null;
  if (
    existingPersona &&
    !existingPersona.isVirtualAdmin &&
    persona.isVirtualAdmin
  ) {
    return state;
  }

  return {
    ...state,
    userByAccountId: {
      ...(state.userByAccountId ?? {}),
      [accountId]: persona,
    },
  };
}

/**
 * Creates a new state with the pending selection updated and userByAccountId cache maintained.
 * Combines caching the next selection's persona with committing it as pending.
 * @param state - The current impersonation state
 * @param nextSelection - The new pending selection to apply
 * @returns Updated state with pending selection and cache updated
 */
export function updateSelectionWithPersona(
  state: ImpersonationState,
  nextSelection: ImpersonationSelection,
): ImpersonationState {
  const withCachedPersona = setPersonaForAccount(
    state,
    nextSelection.account?.id ?? null,
    nextSelection.user ?? null,
  );

  // Not-onboarded selections all collapse to the single "Not Onboarded" tenant
  // option, so cache the account for later recall.
  const withCachedAccount =
    !isOnboardedTenant(nextSelection.tenant.onBoardingStatus)
      ? setPreviewAccount(withCachedPersona, nextSelection.account)
      : withCachedPersona;

  return {
    ...withCachedAccount,
    pending: nextSelection,
  };
}

/**
 * Retrieves the cached persona for a specific account.
 * Returns null if no persona is cached for the account.
 */
export function getPersonaForAccount(
  state: Pick<ImpersonationState, "userByAccountId">,
  accountId: number,
): Persona | null {
  return state.userByAccountId?.[accountId] ?? null;
}

/**
 * Updates the previewAccount cache with the last account selected while not onboarded.
 * Does not update the cache if account is null (early return).
 */
export function setPreviewAccount(
  state: ImpersonationState,
  account: Account | null,
): ImpersonationState {
  if (account == null) {
    return state;
  }

  return {
    ...state,
    previewAccount: account,
  };
}

/**
 * Retrieves the cached preview account, if any.
 */
export function getPreviewAccount(
  state: Pick<ImpersonationState, "previewAccount">,
): Account | null {
  return state.previewAccount ?? null;
}

export function createVirtualAdminPersona(
  accountId: number | null,
): Persona | null {
  if (accountId == null) {
    return null;
  }

  return {
    id: accountId,
    type: "account",
    firstName: "Virtual",
    lastName: "Admin",
    initials: "VA",
    email: null,
    photo: null,
    isGuest: false,
    isVirtualAdmin: true,
    isAdmin: false,
    shiComEmployeeId: null,
  };
}